import React, { useEffect, useState } from 'react';
import { Delete, Edit, Publish, Refresh, Add } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import {
  deleteReleaseNote,
  fetchReleaseNotes,
  publishReleaseNote,
} from '../../modules/admin';
import TableComponent from '../../ui/data/table/TableComponent';
import { ReleaseNoteTableHeaders } from '../project-dashboard/test-case/testCaseTableHead';
import CreateReleaseNoteModal from './CreateReleaseNoteModal';
import ActionButton from '../../ui/primitives/ActionButton';
import ActionMenu from '../../ui/primitives/ActionMenu';
import { useNotification } from '../../hooks/NotificationContext';

const AdminReleaseNotesListPage = () => {
  const [loading, setLoading] = useState(true);
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [totalCount, setTotalCount] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState(null);
  const { addNotification } = useNotification();
  const [mode, setMode] = useState('add');
  const [order, setOrder] = useState('desc');

  const fetchReleaseNotesList = async () => {
    setLoading(true);
    try {
      const response = await fetchReleaseNotes(page, perPage);
      setReleaseNotes(response?.release_notes || []);
      setTotalCount(response?.total || 0);
    } catch (error) {
      console.error('Error fetching release notes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReleaseNotesList();
  }, [page]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleEdit = (releaseNote) => {
    setMode('edit');
    setFormData(releaseNote);
    setShowCreateModal(true);
  };

  const handleDelete = async (releaseNoteId) => {
    setLoading(true);
    try {
      await deleteReleaseNote(releaseNoteId);
      fetchReleaseNotesList();
      addNotification('success', 'Release note deleted successfully!');
    } catch (error) {
      addNotification('danger', 'Failed to delete release note.');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (releaseNoteId) => {
    setLoading(true);
    try {
      await publishReleaseNote(releaseNoteId);
      fetchReleaseNotesList();
      addNotification('success', 'Release note published successfully!');
    } catch (error) {
      addNotification('danger', 'Failed to publish release note.');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setFormData(null);
    setMode('add');
    setShowCreateModal(false);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Release Notes
      </Typography>

      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
        sx={{ mb: 2 }}
      >
        <ActionButton
          label="Create Release Note"
          icon={<Add />}
          intent="primary"
          onClick={() => setShowCreateModal(true)}
        />
        <ActionButton
          icon={<Refresh />}
          intent="secondary"
          onClick={fetchReleaseNotesList}
          tooltip="Click to refresh the list"
        />
      </Stack>

      <TableComponent
        data={releaseNotes}
        headerTabs={ReleaseNoteTableHeaders}
        page={page}
        handlePageChange={handlePageChange}
        perPage={perPage}
        order={order}
        setOrder={setOrder}
        isLoading={loading}
        striped
        totalCount={totalCount}
        showPages
        tablekey="release notes"
        showCount={true}
        action={(row) => {
          const items = [
            {
              label: 'Edit',
              icon: <Edit />,
              onClick: () => handleEdit(row),
            },
          ];
          if (row.published === false) {
            items.push(
              {
                label: 'Publish',
                icon: <Publish />,
                color: 'success',
                disabled: row?.published,
                onClick: () => handlePublish(row.id),
              },
              {
                label: 'Delete',
                icon: <Delete />,
                color: 'error',
                onClick: () => handleDelete(row.id),
              }
            );
          }

          return <ActionMenu items={items} tooltip="Release note actions" />;
        }}
      />

      <CreateReleaseNoteModal
        showModal={showCreateModal}
        handleClose={handleModalClose}
        refreshList={fetchReleaseNotesList}
        initialData={formData}
        mode={mode}
      />
    </Box>
  );
};

export default AdminReleaseNotesListPage;